import { useCallback, useEffect, useState } from 'react';

import PhoneInput from 'react-phone-input-2';

import { TextField, Box, Button, Typography } from '@tradersclub/core-ui';
import 'react-phone-input-2/lib/style.css';

import BoxAuth from 'components/auth/BoxAuth';
import FooterSecurity from 'components/auth/FooterSecurity';
import HeaderLogo from 'components/auth/HeaderLogo';
import { ToastContainerCustom } from 'components/CheckoutV2/Toast/styles';
import FieldsTrackHubspot from 'components/FieldsTrackHubspot';
import Icon from 'components/Icon';
import useFacebookPixelFerri from 'hooks/useFacebookPixel';
import useFacebookPixel from 'hooks/useFacebookPixelData';
import useGA from 'hooks/useGA';
import useRouterStore from 'hooks/useRouter';
import useToaster from 'hooks/useToaster';
import { validateEmail, validateUsername, createUser } from 'services/auth';
import { getBaseUrlLP, getBaseUrl } from 'services/utils';
import useStoreUser from 'store/user';
import { ORIGIN } from 'utils/constants';
import { validatePhone } from 'utils/utils';

import resolverForm, { SubmitForm } from './schema';
import * as S from './styles';

import { getProductDetails } from '../../../services/api';

type OnChangeForm = React.ChangeEvent<HTMLInputElement>;

type UserChangeForm = {
  email: boolean;
  phone: boolean;
  nickname: boolean;
};

const SignUpScreen = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [userChangeForm, setUserChangeForm] = useState({} as UserChangeForm);
  const [hasSendDLCheckout, setHasSendDLCheckout] = useState(false);

  const { trackSubmitApplication, trackLead } = useFacebookPixel();

  const { router, goToSignIn, goToCheckout, goToShowcase } = useRouterStore();
  const { code, origin } = router.query;
  const productId =
    router.query.product_id || router?.asPath.match(new RegExp(`[&?]product_id=(.*)(&|$)`));

  const isLandingPage = origin === ORIGIN.LANDING_PAGE;

  const { signIn, userId } = useStoreUser((state) => ({
    signIn: state.signIn,
    userId: state.id,
  }));

  const { addError } = useToaster();

  const { sendDataLayerSignUp, sendDataLayerCheckout } = useGA({
    isLandingPage,
  });

  // TRACK FACEBOOK - PAGE VIEW
  useFacebookPixelFerri();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = resolverForm();

  const watchForm = watch();

  const redirectUserPage = useCallback(() => {
    if (!isLandingPage) {
      router.replace(goToShowcase());
      return null;
    }

    const urlLP = getBaseUrlLP();
    if (urlLP && window) (window.parent.location || window.location).href = urlLP;
  }, [isLandingPage, goToShowcase, router]);

  const getDetails = useCallback(async () => {
    if (hasSendDLCheckout) return;
    try {
      const data = await getProductDetails(productId, userId);
      sendDataLayerCheckout(data, `Cadastro`, false, 1);
      setHasSendDLCheckout(true);
    } catch (e) {
      console.log(e);
    }
  }, [hasSendDLCheckout, productId, sendDataLayerCheckout, userId]);

  useEffect(() => {
    if (!productId) redirectUserPage();
  }, [productId, redirectUserPage]);

  useEffect(() => {
    getDetails();
  }, [getDetails]);

  useEffect(() => {
    if (validatePhone(watchForm.phone)) clearErrors(`phone`);
    else if (userChangeForm.phone)
      setError(`phone`, {
        message: `Número de celular inválido`,
      });
  }, [watchForm.phone, clearErrors, setError, userChangeForm.phone]);

  useEffect(() => {
    if (watchForm.email?.length >= 5) clearErrors(`email`);
    else if (userChangeForm.email)
      setError(`email`, {
        message: `Insira um email válido!`,
      });
  }, [watchForm.email, clearErrors, setError, userChangeForm.email]);

  useEffect(() => {
    if (watchForm.nickname?.length >= 4) clearErrors(`nickname`);
    else if (userChangeForm.nickname)
      setError(`nickname`, {
        message: `Deve conter no mínimo 4 caracteres!`,
      });
  }, [watchForm.nickname, clearErrors, setError, userChangeForm.nickname]);

  const onSubmit: SubmitForm = async (form) => {
    try {
      if (!userChangeForm.phone) {
        setError(`phone`, {
          message: `Número de celular inválido`,
        });
        return;
      }

      const existEmail = await validateEmail(form.email);
      if (existEmail) {
        setError(`email`, {
          message: `Este email já está cadastrado!`,
        });
        return;
      }
      const existUser = await validateUsername(form.nickname);
      if (existUser) {
        setError(`nickname`, {
          message: `Este usuário já existe!`,
        });
        return;
      }

      const resp = await createUser(form);
      if (resp?.status === 201) {
        const loggedUser = await signIn({
          loginId: form.nickname,
          password: form.pass,
          origin: `Cadastro`,
        });

        trackLead();

        sendDataLayerSignUp({
          userId: loggedUser.id,
          userEmail: loggedUser.email,
        });

        if (productId && +productId === 44) {
          const url = `${getBaseUrl()}/tradersclub/home`;
          (window?.parent.location || window?.location).href = url;
        } else {
          router.replace(goToCheckout(productId));
        }
      }
    } catch (e) {
      addError(`Ocorreu um erro, revise seus dados.`);
    }
  };

  const changeEmail = (e: OnChangeForm): void => {
    const value = e.target.value || ``;
    setValue(`email`, value);
    setUserChangeForm({ ...userChangeForm, email: true });

    const { nickname } = getValues();

    if (!userChangeForm.nickname || !nickname?.length) {
      const newNickname = value.split(`@`)[0];
      if (newNickname !== nickname) setValue(`nickname`, newNickname);
    }
  };

  const changeNickname = (e: OnChangeForm): void => {
    const { value } = e.target;
    setValue(`nickname`, value);
    if (!userChangeForm.nickname) setUserChangeForm({ ...userChangeForm, nickname: true });
  };

  const onFocusPhone = (): void => {
    if (!userChangeForm.phone) {
      setValue(`phone`, `55`);
      setUserChangeForm({ ...userChangeForm, phone: true });
    }
  };

  function trackFocus() {
    trackSubmitApplication();
  }

  return (
    <S.Wrapper>
      <ToastContainerCustom autoClose={2000} />
      <S.Content>
        <HeaderLogo productId={Number(productId)} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <BoxAuth code={code}>
            <Box paddingBottom={32}>
              <TextField
                width="100%"
                type="text"
                placeholder="Nome completo"
                error={!!errors?.name}
                helperTextLeft={errors?.name?.message}
                onFocus={() => trackFocus()}
                {...register(`name`)}
              />
            </Box>
            <Box paddingBottom={32}>
              <TextField
                width="100%"
                type="text"
                placeholder="Email"
                name="email"
                error={!!errors?.email}
                helperTextLeft={errors?.email?.message}
                onChange={changeEmail}
              />
            </Box>
            <Box paddingBottom={32}>
              <S.BoxCellphone error={!!errors?.phone}>
                <PhoneInput
                  placeholder="+99 (99) 99999-9999"
                  disableDropdown
                  containerClass="custom-phone-container"
                  inputClass="custom-phone-input"
                  buttonClass="custom-phone-button"
                  value={getValues(`phone`) || ``}
                  onChange={(v) => setValue(`phone`, v)}
                  onFocus={onFocusPhone}
                  masks={{ br: `(..) .....-....` }}
                />
                {errors?.phone && (
                  <S.ErrorInput>
                    <Icon name="AlertFill" size="small" color="#D82259" />
                    {errors?.phone?.message}
                  </S.ErrorInput>
                )}
              </S.BoxCellphone>
            </Box>
            <Box paddingBottom={32}>
              <TextField
                width="100%"
                type="text"
                placeholder="Usuário"
                name="nickname"
                error={!!errors?.nickname}
                helperTextLeft={errors?.nickname?.message}
                value={watchForm.nickname}
                onChange={changeNickname}
              />
            </Box>
            <Box paddingBottom={32}>
              <TextField
                width="100%"
                type={showPassword ? `text` : `password`}
                placeholder="Senha"
                error={!!errors?.pass}
                helperTextLeft={errors?.pass?.message}
                {...register(`pass`)}
                endAdornment={
                  <Box style={{ cursor: `pointer` }} onClick={() => setShowPassword(!showPassword)}>
                    <Icon name={showPassword ? `Hide` : `Show`} color="#11161C" />
                  </Box>
                }
              />
            </Box>

            <Box display="flex" alignItems="center" justifyContent="center" paddingBottom={32}>
              <Button
                disabled={isSubmitting}
                variant="primary"
                size="medium"
                type="submit"
                width="100%"
              >
                Criar Conta
              </Button>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
              <Typography variant="body1" textColor="text.tertiary">
                Já possui cadastro?
              </Typography>
              <S.NextLink href={goToSignIn(productId)}>Clique aqui e faça login.</S.NextLink>
            </Box>
          </BoxAuth>
          <FieldsTrackHubspot />
        </form>
        <FooterSecurity />
      </S.Content>
    </S.Wrapper>
  );
};

export default SignUpScreen;
